import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  FileText,
  Download,
  ArrowRight,
  Clock,
  Eye,
  Mail
} from 'lucide-react'
import SEO from '@components/SEO'
import { useContent } from '@context/ContentContext'
import Reveal from '@components/dimensional/Reveal'
import TiltCard from '@components/dimensional/TiltCard'
import BrandPrimitives from '@components/dimensional/BrandPrimitives'
import RewardsArchitecture from '@components/dimensional/RewardsArchitecture'

const ResourcesPage = () => {
  const { content, loading } = useContent()

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: 'easeOut' }
  }

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  // Get page content
  const pageContent = content.pages?.resources || {}
  const heroTitle = pageContent.hero?.title || 'Insights & Resources'
  const heroSubtitle = pageContent.hero?.subtitle || 'Benchmarks, plan mechanics and post-mortems pulled straight out of live engagements. Written for the people who have to defend the numbers in the room.'

  const featuredResources = [
    {
      type: 'Whitepaper',
      title: 'Equity Compensation: What Actually Changed This Cycle',
      description: 'Valuation practice, refresh grant sizing, and the plan designs that survived a down round. Includes the regulatory shifts that quietly reset how pools get sized.',
      image: '📊',
      downloadCount: '2.3K',
      readTime: '15 min',
      category: 'Equity Compensation',
      featured: true
    },
    {
      type: 'Case Study',
      title: 'Cutting Regretted Attrition by 40% at a Fast-Growing Fintech',
      description: 'What actually changed: band structure, refresh cadence, and a manager briefing pack. The sequence we ran, and the two things that did not work.',
      image: '🚀',
      downloadCount: '1.8K',
      readTime: '12 min',
      category: 'Case Studies',
      featured: true
    },
    {
      type: 'Research Report',
      title: 'Where APAC, EMEA and Americas Pay Really Diverge',
      description: 'The medians that genuinely differ, the ones that only look like they do, and how to run a single global band structure without paying a local premium three times over.',
      image: '🌍',
      downloadCount: '3.1K',
      readTime: '20 min',
      category: 'Market Research',
      featured: true
    }
  ]

  const blogPosts = [
    {
      title: 'What Boards Are Actually Asking About Executive Pay',
      excerpt: 'Scrutiny has moved from quantum to structure. Three questions NRCs keep raising, and how to answer each one with evidence rather than a peer chart.',
      author: 'Sarah Chen',
      date: '2024-01-15',
      readTime: '8 min',
      category: 'Executive Compensation',
      views: '1.2K'
    },
    {
      title: 'Five Mistakes That Sink an ESOP Rollout',
      excerpt: 'Pool sizing settled after the offer goes out. Cliffs nobody explained. Exercise windows that turn a reward into a tax bill. Every one of these is avoidable.',
      author: 'Michael Rodriguez',
      date: '2024-01-10',
      readTime: '6 min',
      category: 'Equity Compensation',
      views: '890'
    },
    {
      title: 'Skills-Based Pay: Where It Works, Where It Breaks',
      excerpt: 'Paying for capability instead of job title reads clean on a slide. It stops being clean the moment you have to price a skill nobody can assess consistently.',
      author: 'Priya Sharma',
      date: '2024-01-05',
      readTime: '10 min',
      category: 'Compensation Strategy',
      views: '1.5K'
    },
    {
      title: 'What Your Competitors Quietly Pay For in Benefits',
      excerpt: 'Findings from this year’s survey: where medical spend is heading, which wellness lines get used, and the perks employees have stopped noticing entirely.',
      author: 'David Thompson',
      date: '2023-12-28',
      readTime: '7 min',
      category: 'Benefits',
      views: '2.1K'
    }
  ]

  const whitepapers = [
    {
      title: 'Complete Guide to 409A Valuations',
      description: 'Method, timing, safe harbour, and the mistakes that force a re-run. Built for finance and HR to read in the same sitting.',
      pages: 45,
      category: 'Equity Compensation',
      downloadCount: '5.2K'
    },
    {
      title: 'Executive Compensation Governance Framework',
      description: 'Board oversight that holds up under questioning: NRC charters, peer group construction, and the disclosure trail an auditor expects to find.',
      pages: 32,
      category: 'Executive Compensation',
      downloadCount: '3.8K'
    },
    {
      title: 'Global Mobility Compensation Handbook',
      description: 'Assignment packages across tax regimes. Balance-sheet and host-based approaches, plus the point at which you should stop using either.',
      pages: 58,
      category: 'Global Compensation',
      downloadCount: '2.9K'
    },
    {
      title: 'Pay Equity Analysis Methodology',
      description: 'Regression setup, cohort definition, and how to close a gap without opening a new one somewhere adjacent.',
      pages: 28,
      category: 'Compensation Strategy',
      downloadCount: '4.1K'
    }
  ]

  const caseStudies = [
    {
      company: 'TechFlow Innovations',
      industry: 'Fintech',
      challenge: 'Senior engineers leaving early. Offers losing to competitors with better-structured equity.',
      solution: 'Rebuilt the bands, added an annual refresh cycle, and rewrote the grant letter so a candidate could read it once and understand it.',
      results: ['Regretted attrition down', 'Senior offers closing faster', 'Equity understood, not just granted'],
      readTime: '12 min'
    },
    {
      company: 'GrowthCorp',
      industry: 'SaaS',
      challenge: 'Executive pay had to survive investor and proxy-adviser scrutiny before the listing window opened.',
      solution: 'Peer group rebuilt from first principles, with the NRC charter and disclosure pack drafted alongside it.',
      results: ['Listed with no pay-related hold-up', 'Strong say-on-pay support', 'Board briefed ahead of every question'],
      readTime: '15 min'
    },
    {
      company: 'DataVision',
      industry: 'Analytics',
      challenge: 'New markets opening faster than any shared pay structure could be built underneath them.',
      solution: 'One global band architecture, with local benefits and statutory compliance layered under it rather than bolted on.',
      results: ['Expansion without renegotiating every offer', 'Local premiums paid once, not layered', 'One levelling language across markets'],
      readTime: '10 min'
    }
  ]

  const categories = [
    'All Resources',
    'Equity Compensation',
    'Executive Compensation',
    'Global Compensation',
    'Benefits',
    'Market Research',
    'Case Studies'
  ]

  // Stats removed - focus on resource quality over quantity

  return (
    <div className="pt-14 bg-black">
      <SEO
        title="HR Resources & Insights - Comprehensive Total Rewards Research"
        description="Access comprehensive HR resources, whitepapers, and strategic insights across all 9 service domains. Learn from real client transformations and download practical guides for performance appraisals, executive compensation, and compliance."
        keywords="HR resources, comprehensive HR insights, performance appraisal guides, executive compensation whitepapers, HR compliance resources, variable pay design guides, benefits design resources, MECE framework resources, total rewards research"
      />

      {/* Hero Section */}
      <section className="pt-16 lg:pt-20 pb-20 lg:pb-24 text-white relative overflow-hidden grain">
        {/* Background Image with WebP fallback */}
        <picture className="absolute inset-0">
          <source srcSet="/resources-hero.webp" type="image/webp" />
          <img
            src="/resources-hero.jpg"
            alt="Modern corporate office workspace"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            decoding="async"
          />
        </picture>

        {/* Depth stack: base darkening, drifting gold mesh, then a vignette that
            pushes the frame edges back and holds the eye on the headline. */}
        <div
          className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/75 to-black/95 pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 mesh-drift opacity-70 pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              'radial-gradient(120% 82% at 50% 8%, transparent 26%, rgba(0,0,0,0.55) 62%, rgba(0,0,0,0.92) 100%)'
          }}
        />

        <div className="w-full px-6 sm:px-8 lg:px-12 relative">
          <motion.div
            className="text-center max-w-page mx-auto"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div
              className="inline-flex items-center gap-3 surface-raised-gold edge-lit rounded-full px-5 py-2.5 mb-9"
              variants={fadeInUp}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 shadow-[0_0_10px_rgba(255,209,24,0.9)]" aria-hidden="true"></span>
              <span className="text-mono-label text-primary-300">
                Research &amp; Field Notes
              </span>
            </motion.div>

            <motion.h1
              className="text-display-xl text-white mb-8 text-balance"
              variants={fadeInUp}
            >
              {heroTitle}
            </motion.h1>

            <motion.p
              className="text-body-large text-neutral-300 mb-10 max-w-3xl mx-auto"
              variants={fadeInUp}
            >
              {heroSubtitle}
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              variants={fadeInUp}
            >
              <Link to="/contact" className="btn-accent sheen px-8 py-3.5">
                Talk to a consultant
                <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
              </Link>
              <button
                onClick={() => {
                  const element = document.getElementById('featured')
                  if (element) {
                    const navbarHeight = 80
                    const elementPosition = element.offsetTop - navbarHeight
                    window.scrollTo({
                      top: elementPosition,
                      behavior: 'smooth'
                    })
                  }
                }}
                className="inline-flex items-center justify-center rounded-md px-8 py-3.5 text-sm font-semibold text-white surface-raised sheen border border-white/10 transition-colors duration-300 hover:border-primary-500/60 hover:text-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black font-open-sans"
              >
                Browse the library
              </button>
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 hairline" aria-hidden="true"></div>
      </section>

      {/* Topic index — pressed into the page rather than floating on it */}
      <section className="bg-black relative overflow-hidden py-12 lg:py-14" aria-label="Topics covered">
        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <div className="surface-inset rounded-card-lg border-x border-b border-white/5 px-8 py-7">
            <p className="text-mono-label text-primary-500 mb-5">
              What the library covers
            </p>
            <ul className="flex flex-wrap gap-x-3 gap-y-3">
              {categories
                .filter((category) => category !== 'All Resources')
                .map((category) => (
                  <li
                    key={category}
                    className="text-body-small text-neutral-300 surface-raised rounded-pill px-4 py-1.5"
                  >
                    {category}
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 bg-black relative overflow-hidden grain ambient-gold">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-5xl mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-10 lg:gap-14 items-center">
            <Reveal>
              <BrandPrimitives
                variant="cluster"
                className="w-full aspect-square max-w-[320px] mx-auto"
                label="Three metallic primitives — a cube, a cone and a sphere — turning slowly in the brand palette."
              />
              <p className="text-mono-label text-primary-500 mt-6 mb-3 text-center lg:text-left">
                Why we publish
              </p>
              <p className="text-body-small text-neutral-400 text-center lg:text-left">
                The same few pieces, arranged differently for every organisation. The library is a
                record of those arrangements and what each one cost to build.
              </p>
            </Reveal>

            <Reveal className="text-center lg:text-left" delay={110}>
              <div className="surface-raised-gold edge-lit sheen rounded-card-lg px-8 py-10 lg:px-12">
                <blockquote className="text-xl lg:text-2xl text-primary-300 font-semibold mb-5 font-open-sans tracking-tight text-balance">
                  &ldquo;A benchmark you cannot defend in a board meeting is just a number.&rdquo;
                </blockquote>
                <p className="text-base text-neutral-300 leading-relaxed text-balance">
                  Everything here started as client work — a merit matrix that had to survive a budget
                  cut, a 409A that had to hold up under audit. We publish the method, not the pitch.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Featured Resources */}
      <section id="featured" className="py-20 lg:py-24 bg-black relative overflow-hidden grain scroll-mt-20">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <Reveal className="max-w-3xl mb-14 lg:mb-16">
            <p className="text-mono-label text-primary-500 mb-5">
              <span className="text-neutral-500">01 / 04</span>
              <span className="mx-3 text-neutral-700" aria-hidden="true">&mdash;</span>
              Featured
            </p>
            <h2 className="text-section-title text-white mb-6 text-balance">
              Start here.
            </h2>
            <p className="text-body-large text-neutral-300">
              The three most-downloaded pieces in the library. Each one carries the working, not
              just the conclusion.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {featuredResources.map((resource, index) => (
              // `.scene` sits on the Reveal, not the grid: perspective only
              // reaches direct children, and the Reveal's own transform would
              // otherwise flatten the card's 3D space before it renders.
              <Reveal key={index} className="scene h-full" delay={index * 90}>
                {/* No `.sheen` here — its overflow:hidden would flatten
                    preserve-3d and cancel the .tilt-raise lift. */}
                <TiltCard className="surface-raised edge-lit rounded-card-lg h-full group">
                  <div className="depth-layer p-8 h-full flex flex-col">
                    <div className="flex items-start justify-between mb-7">
                      <div className="w-14 h-14 surface-raised-gold rounded-xl flex items-center justify-center text-2xl tilt-raise" aria-hidden="true">
                        {resource.image}
                      </div>
                      <span className="text-mono-label text-primary-400 border border-primary-500/30 rounded-full px-3 py-1">
                        {resource.type}
                      </span>
                    </div>

                    <h3 className="text-heading-1 text-white mb-4 text-balance">
                      {resource.title}
                    </h3>
                    <p className="text-body-small text-neutral-400 mb-7">{resource.description}</p>

                    <div className="mt-auto">
                      <div className="hairline mb-6" aria-hidden="true"></div>

                      <div className="flex items-center justify-between text-mono-label text-neutral-500 mb-7">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1.5">
                            <Download className="w-3.5 h-3.5" aria-hidden="true" />
                            <span>{resource.downloadCount}</span>
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                            <span>{resource.readTime}</span>
                          </span>
                        </div>
                        <span className="text-neutral-500">{resource.category}</span>
                      </div>

                      <button className="btn-accent sheen w-full justify-center">
                        Download
                        <span className="sr-only"> {resource.title}</span>
                        <Download className="w-4 h-4 ml-2" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How the library maps onto the rewards stack */}
      <section className="py-20 lg:py-24 bg-black relative overflow-hidden grain" aria-labelledby="library-stack-heading">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <p className="text-mono-label text-primary-500 mb-5">
                How it is organised
              </p>
              <h2 id="library-stack-heading" className="text-section-title text-white mb-6 text-balance">
                Four layers, one library
              </h2>
              <p className="text-body-large text-neutral-300 mb-6">
                Base pay, benefits, variable pay, equity. Every whitepaper and case study here sits
                on one of those layers — or on the seam between two of them.
              </p>
              <p className="text-body text-neutral-400">
                Read down the stack if you are rebuilding a structure. Read across it if the problem
                is that a change in one layer keeps surfacing somewhere else.
              </p>
            </Reveal>

            <Reveal delay={110}>
              <div className="surface-raised edge-lit rounded-card-lg p-6 sm:p-8">
                <RewardsArchitecture className="w-full h-auto max-w-[460px] mx-auto" />
              </div>
              <p className="text-body-small text-neutral-500 mt-4 text-center">
                Illustrative stack. The proportions differ by sector and by seniority.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Latest Blog Posts */}
      <section className="py-20 lg:py-24 bg-black relative overflow-hidden grain ambient-gold">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <Reveal className="max-w-3xl mb-14 lg:mb-16">
            <p className="text-mono-label text-primary-500 mb-5">
              <span className="text-neutral-500">02 / 04</span>
              <span className="mx-3 text-neutral-700" aria-hidden="true">&mdash;</span>
              From the desk
            </p>
            <h2 className="text-section-title text-white mb-6 text-balance">
              Latest insights
            </h2>
            <p className="text-body-large text-neutral-300">
              Short pieces on what we are seeing in live engagements — band pressure, incentive
              mechanics, and the regulation shifting underneath both.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {blogPosts.map((post, index) => (
              <Reveal key={index} className="h-full" delay={index * 80}>
                <article className="surface-raised edge-lit rounded-card-lg p-8 h-full flex flex-col group">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-mono-label text-primary-400 border border-primary-500/30 rounded-full px-3 py-1">
                      {post.category}
                    </span>
                    <span className="flex items-center gap-1.5 text-mono-label text-neutral-500">
                      <Eye className="w-3.5 h-3.5" aria-hidden="true" />
                      <span>{post.views}</span>
                    </span>
                  </div>

                  <h3 className="text-heading-1 text-white mb-4 text-balance">
                    {post.title}
                  </h3>
                  <p className="text-body-small text-neutral-400 mb-8">{post.excerpt}</p>

                  <div className="mt-auto">
                    <div className="hairline mb-6" aria-hidden="true"></div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 gradient-premium-gold rounded-full flex items-center justify-center text-navy-950 font-bold text-xs premium-shadow" aria-hidden="true">
                          {post.author.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <p className="font-semibold text-white text-sm font-open-sans">{post.author}</p>
                          <div className="flex items-center gap-2 text-mono-label text-neutral-500">
                            <span>{new Date(post.date).toLocaleDateString()}</span>
                            <span aria-hidden="true">•</span>
                            <span>{post.readTime}</span>
                          </div>
                        </div>
                      </div>

                      <button className="p-2 rounded-md text-primary-400 hover:text-primary-300 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black">
                        <span className="sr-only">Read {post.title}</span>
                        <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal className="text-center mt-14" delay={120}>
            <Link
              to="/blog"
              className="inline-flex items-center justify-center rounded-md px-8 py-3.5 text-sm font-semibold text-white surface-raised sheen border border-white/10 transition-colors duration-300 hover:border-primary-500/60 hover:text-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black font-open-sans"
            >
              View all articles
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Whitepapers & Research */}
      <section className="py-20 lg:py-24 bg-black relative overflow-hidden grain">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          {/* Opener into the long-form half of the page */}
          <div className="surface-inset rounded-card-lg border-x border-b border-white/5 px-6 py-8 sm:px-8 sm:py-10 mb-14 lg:mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-8 lg:gap-12 items-center">
              <Reveal>
                <p className="text-mono-label text-primary-500 mb-4">
                  Long form, from here down
                </p>
                <h2 className="text-heading-1 text-white mb-4 text-balance">
                  Built to be worked from
                </h2>
                <p className="text-body text-neutral-400">
                  Everything below is method rather than summary: how the analysis was set up, what
                  the assumptions were, and the parts of the sequence we would change next time.
                </p>
              </Reveal>

              <Reveal delay={110}>
                <BrandPrimitives
                  variant="row"
                  className="w-full aspect-square max-w-[280px] mx-auto"
                  label="A cone, a sphere and a cube set out in a row, marking the long-form section of the library."
                />
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Whitepapers */}
            <Reveal>
              <p className="text-mono-label text-primary-500 mb-5">
                <span className="text-neutral-500">03 / 04</span>
                <span className="mx-3 text-neutral-700" aria-hidden="true">&mdash;</span>
                Long form
              </p>
              <h2 className="text-section-title text-white mb-5 text-balance">Research &amp; whitepapers</h2>
              <p className="text-body text-neutral-400 mb-8">
                Method documents, written to be kept open during a design cycle rather than read once
                and filed.
              </p>

              <div className="space-y-5">
                {whitepapers.map((paper, index) => (
                  <div key={index} className="surface-raised edge-lit rounded-card p-6 transition-colors duration-500 hover:bg-white/[0.02]">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-heading-1 text-white mb-2">{paper.title}</h3>
                        <p className="text-body-small text-neutral-400 mb-5">{paper.description}</p>

                        <div className="flex flex-wrap items-center gap-4 text-mono-label text-neutral-500">
                          <span className="flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5" aria-hidden="true" />
                            <span>{paper.pages} pages</span>
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Download className="w-3.5 h-3.5" aria-hidden="true" />
                            <span>{paper.downloadCount}</span>
                          </span>
                          <span className="text-neutral-500">{paper.category}</span>
                        </div>
                      </div>

                      <button className="p-2.5 rounded-lg text-primary-400 surface-inset hover:text-primary-300 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black">
                        <span className="sr-only">Download {paper.title}</span>
                        <Download className="w-5 h-5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Case Studies */}
            <Reveal delay={110}>
              <p className="text-mono-label text-primary-500 mb-5">
                <span className="text-neutral-500">04 / 04</span>
                <span className="mx-3 text-neutral-700" aria-hidden="true">&mdash;</span>
                Engagements
              </p>
              <h2 className="text-section-title text-white mb-5 text-balance">Case studies</h2>
              <p className="text-body text-neutral-400 mb-8">
                What changed, what it cost, and what we would sequence differently a second time.
              </p>

              <div className="space-y-5">
                {caseStudies.map((study, index) => (
                  <div key={index} className="surface-raised edge-lit rounded-card p-6 group">
                    <div className="flex items-center justify-between gap-4 mb-6">
                      <h3 className="text-heading-1 text-white">{study.company}</h3>
                      <span className="text-mono-label text-primary-400 border border-primary-500/30 rounded-full px-3 py-1 whitespace-nowrap">
                        {study.industry}
                      </span>
                    </div>

                    <div className="space-y-5">
                      <div>
                        <h4 className="text-mono-label text-neutral-500 mb-2">Challenge</h4>
                        <p className="text-body-small text-neutral-300">{study.challenge}</p>
                      </div>

                      <div>
                        <h4 className="text-mono-label text-neutral-500 mb-2">Solution</h4>
                        <p className="text-body-small text-neutral-300">{study.solution}</p>
                      </div>

                      <div className="surface-inset rounded-xl p-5">
                        <h4 className="text-mono-label text-primary-500 mb-3">Results</h4>
                        <ul className="space-y-2">
                          {study.results.map((result, idx) => (
                            <li key={idx} className="flex items-start gap-3 text-body-small text-neutral-300">
                              <span className="w-1 h-1 mt-2.5 rounded-full bg-primary-500 flex-shrink-0" aria-hidden="true"></span>
                              <span>{result}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="hairline" aria-hidden="true"></div>

                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-mono-label text-neutral-500">
                          <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                          <span>{study.readTime}</span>
                        </span>
                        <button className="p-2 rounded-md text-primary-400 hover:text-primary-300 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black">
                          <span className="sr-only">Read the {study.company} case study</span>
                          <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 lg:py-24 bg-black text-white relative overflow-hidden grain ambient-gold">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>
        <div
          className="absolute inset-0 mesh-drift opacity-40 pointer-events-none"
          aria-hidden="true"
        />

        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-14 items-center">
            <Reveal>
              <BrandPrimitives
                variant="column"
                className="w-full aspect-square max-w-[300px] mx-auto"
                label="A sphere, a cone and a cube stacked in a slowly turning column."
              />
              <p className="text-body-small text-neutral-400 mt-6 text-center lg:text-left">
                One issue a month, sent on the same schedule the library is updated on.
              </p>
            </Reveal>

            <Reveal delay={110}>
              <div className="surface-raised-gold edge-lit rounded-arc px-8 py-12 lg:px-14 lg:py-14 text-center lg:text-left">
                <div className="w-12 h-12 gradient-premium-gold rounded-xl flex items-center justify-center mx-auto lg:mx-0 mb-7 premium-shadow">
                  <Mail className="w-5 h-5 text-navy-950" aria-hidden="true" />
                </div>

                <h2 className="text-display-lg text-white mb-6 text-balance">
                  The monthly brief
                </h2>
                <p className="text-body-large text-neutral-300 mb-9 max-w-2xl mx-auto lg:mx-0 text-balance">
                  One email a month. A benchmark worth reading, a plan design pulled apart, and whatever
                  moved in regulation. No link round-ups.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto lg:mx-0 mb-6">
                  <label htmlFor="resources-newsletter-email" className="sr-only">
                    Email address
                  </label>
                  <input
                    id="resources-newsletter-email"
                    type="email"
                    placeholder="Enter your email address"
                    className="flex-1 min-w-0 px-6 py-4 rounded-md text-navy-900 placeholder-neutral-500 focus:outline-none focus:ring-4 focus:ring-primary-500"
                  />
                  <button className="btn-accent sheen whitespace-nowrap">
                    Subscribe
                    <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
                  </button>
                </div>

                <p className="text-sm text-neutral-400">
                  Read by 5,000+ compensation professionals. Unsubscribe anytime.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ResourcesPage
